/**
 * The estimate calculator on a model's page.
 *
 * It always shows which catalog version and rule produced the number. When part
 * of the usage has no known rate it shows the part it does know, names the part
 * it does not, and withholds the total — the subtotal is never presented as if
 * it were the answer.
 */

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";

import type { EstimateResult, RateSet, TokenBucket } from "@pricing/contracts";
import { client } from "../lib/api.ts";
import { BUCKET_LABELS, formatUsd } from "../lib/format.ts";

const BUCKETS: readonly TokenBucket[] = [
  "input",
  "output",
  "reasoning",
  "cache_read",
  "cache_write",
  "cache_write_5m",
  "cache_write_1h",
];

export interface EstimateCalculatorProps {
  readonly offeringId: string;
  readonly catalogRevisionId: string | null;
  readonly atUtc?: string;
  readonly sourceId?: string;
  /**
   * The offering's current rates, when known. Buckets this seller does not
   * bill get no input: tokens typed there could only withhold the total.
   */
  readonly rateSet?: RateSet;
}

export function EstimateCalculator(props: EstimateCalculatorProps): React.JSX.Element {
  const [usage, setUsage] = useState<Partial<Record<TokenBucket, string>>>({
    input: "1000000",
    output: "100000",
  });

  // A bucket this seller does not bill gets no input, unless something was
  // already typed there before the rates arrived: hiding it then would drop
  // those tokens from the estimate and make them look free. It stays visible
  // so the value can be seen and cleared, and it is sent, so the answer says
  // the seller does not bill it and withholds the total.
  const buckets = BUCKETS.filter(
    (bucket) =>
      props.rateSet?.[bucket].state !== "not_billed" || (usage[bucket] ?? "").trim() !== "",
  );

  const estimate = useMutation({
    mutationFn: async () => {
      const counts: Partial<Record<TokenBucket, number>> = {};
      // Every bucket with tokens typed in it, shown or not.
      for (const bucket of BUCKETS) {
        const raw = usage[bucket];
        if (raw === undefined || raw.trim() === "") continue;
        const value = Number(raw);
        if (!Number.isSafeInteger(value) || value < 0) {
          throw new Error(`${BUCKET_LABELS[bucket]} must be a whole number of tokens, zero or more.`);
        }
        counts[bucket] = value;
      }
      return client.estimates({
        ...(props.catalogRevisionId ? { catalog_revision_id: props.catalogRevisionId } : {}),
        ...(props.atUtc ? { at_utc: props.atUtc } : {}),
        ...(props.sourceId ? { source_id: props.sourceId } : {}),
        items: [{ id: "calculator", offering_id: props.offeringId, usage: counts }],
      });
    },
  });

  const result = estimate.data?.data?.[0] as EstimateResult | undefined;

  return (
    <section className="panel">
      <h2 className="panel__title">Estimate a cost</h2>
      <p className="stat__denominator" style={{ marginBottom: "var(--space-3)" }}>
        Token counts must not overlap: reasoning and cache tokens are counted separately from input
        and output.
      </p>

      <form
        className="calculator"
        onSubmit={(event) => {
          event.preventDefault();
          estimate.mutate();
        }}
      >
        {buckets.map((bucket) => (
          <label key={bucket} className="field">
            {BUCKET_LABELS[bucket]} tokens
            <input
              type="number"
              min={0}
              step={1}
              inputMode="numeric"
              value={usage[bucket] ?? ""}
              onChange={(event) => setUsage((current) => ({ ...current, [bucket]: event.target.value }))}
            />
          </label>
        ))}
        <div className="field" style={{ justifyContent: "flex-end" }}>
          <span aria-hidden="true">&nbsp;</span>
          <button type="submit" className="button button--primary" disabled={estimate.isPending}>
            {estimate.isPending ? "Calculating…" : "Calculate"}
          </button>
        </div>
      </form>

      {estimate.isError ? (
        <p className="notice notice--danger" role="alert" style={{ marginTop: "var(--space-4)" }}>
          {(estimate.error as Error).message}
        </p>
      ) : null}

      {result ? (
        <div style={{ marginTop: "var(--space-4)", display: "grid", gap: "var(--space-3)" }}>
          {result.total_usd !== null ? (
            <p className="result" aria-live="polite">
              {formatUsd(result.total_usd)}
            </p>
          ) : (
            <div className="notice notice--warn" aria-live="polite">
              <p className="result--withheld">
                Total withheld — known subtotal {formatUsd(result.known_subtotal_usd)}
              </p>
              <p>
                {result.unpriced_buckets.length === 0
                  ? "No applicable price was found for this offering at the requested time."
                  : null}
                {describeUnpriced(result, "not_billed", "Not billed by this seller")}
                {describeUnpriced(result, "unknown", "No rate is known for")}
                {describeUnpriced(result, "unsupported", "Not covered")}
              </p>
            </div>
          )}

          {result.contributions.length > 0 ? (
            <div className="table-wrap">
              <table>
                <caption className="visually-hidden">Contribution of each token bucket</caption>
                <thead>
                  <tr>
                    <th scope="col">Bucket</th>
                    <th scope="col" className="numeric">
                      Tokens
                    </th>
                    <th scope="col" className="numeric">
                      Rate / 1M
                    </th>
                    <th scope="col" className="numeric">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {result.contributions.map((contribution) => (
                    <tr key={contribution.bucket}>
                      <th scope="row">
                        {BUCKET_LABELS[contribution.bucket]}
                        {/* Only worth a line when the bucket is billed at another's rate. */}
                        {contribution.billed_via !== contribution.bucket ? (
                          <div className="stat__denominator">
                            billed as {BUCKET_LABELS[contribution.billed_via].toLowerCase()}
                          </div>
                        ) : null}
                      </th>
                      <td className="numeric">{contribution.tokens.toLocaleString()}</td>
                      <td className="numeric">
                        {formatUsd(contribution.effective_rate_usd_per_million)}
                      </td>
                      <td className="numeric">{formatUsd(contribution.amount_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          <dl className="context-bar">
            <div>
              <dt className="visually-hidden">Catalog version</dt>
              <dd style={{ margin: 0 }}>
                catalog <code>{result.provenance.catalog_revision_id}</code>
              </dd>
            </div>
            <div>
              <dt className="visually-hidden">Rule</dt>
              <dd style={{ margin: 0 }}>
                rule <code>{result.provenance.rule_id ?? "none"}</code>
              </dd>
            </div>
            <div>
              <dt className="visually-hidden">Source</dt>
              <dd style={{ margin: 0 }}>
                source <code>{result.provenance.source_id ?? "none"}</code>
              </dd>
            </div>
            <div>
              <dt className="visually-hidden">Calculation policy</dt>
              <dd style={{ margin: 0 }}>
                policy <code>{result.provenance.calculation_policy_version}</code>
              </dd>
            </div>
          </dl>

          {result.warnings.length > 0 ? (
            <ul style={{ margin: 0, paddingLeft: "var(--space-6)", fontSize: "var(--text-sm)" }}>
              {result.warnings.map((warning, index) => (
                <li key={`${warning.code}-${index}`}>
                  <strong>{warning.code.replace(/_/g, " ")}:</strong> {warning.message}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

/**
 * One sentence per kind of unpriced bucket. "Not billed" is a different fix
 * from "unknown": the tokens belong in a bucket the seller does bill.
 */
function describeUnpriced(
  result: EstimateResult,
  state: EstimateResult["unpriced_buckets"][number]["rate_state"],
  lead: string,
): string | null {
  const entries = result.unpriced_buckets.filter((entry) => entry.rate_state === state);
  if (entries.length === 0) return null;
  const list = entries
    .map((entry) => `${BUCKET_LABELS[entry.bucket]} (${entry.tokens.toLocaleString()} tokens)`)
    .join(", ");
  const advice = state === "not_billed" ? " Count those tokens in the bucket the seller bills." : "";
  return `${lead}: ${list}.${advice} `;
}
